import type { RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { datadog } from '@sg/expo-datadog-client';
import { useNoticeBannersStackActions } from '@sg/garnish';

import { getAwsWafToken } from '@order/modules-aws-waf';
import { useTelemetry } from '@order/Telemetry';

import { getErrorMessage, initializeAwsWafNativeSdk, withTimeout } from '../../utils';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generates AWS WAF tokens with the native AWS WAF mobile SDK.
 */
export const AwsWafNativeToken = (props: Props) => {
  const { generateAwsWafTokenRef } = props;

  const { push: addNoticeBanner } = useNoticeBannersStackActions();
  const { formatMessage } = useIntl();
  const { track } = useTelemetry();

  // ─── Refs ──────────────────────────────────────────────────────────────────

  // To reuse the same promise if a new request is made while the previous one is still pending.
  const tokenPromiseRef = useRef<Promise<string> | undefined>(undefined);

  // Like the WebView flow, stops generating tokens and showing errors once the form is gone.
  const isUnmountedRef = useRef(false);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const trackMountState = useCallback(() => {
    isUnmountedRef.current = false;

    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  generateAwsWafTokenRef.current = () => {
    if (isUnmountedRef.current) {
      return Promise.resolve('');
    }

    if (tokenPromiseRef.current) {
      return tokenPromiseRef.current;
    }

    track('waf.token.debug', { source: TOKEN_SOURCE, isRequestingToken: true, isFinal: false });

    const startedAt = performance.now();

    const tokenPromise = (async () => {
      try {
        const token = await withTimeout(requestAwsWafToken(), TOKEN_RESPONSE_TIMEOUT_MS, 'Token timeout'); // prettier-ignore

        if (isUnmountedRef.current) {
          return '';
        }

        const duration = performance.now() - startedAt;

        track('waf.token.debug', { source: TOKEN_SOURCE, isFinal: true, hasToken: true, duration });
        datadog.logger.info('AWS WAF | Token ready', { tokenSource: TOKEN_SOURCE, duration });

        return token;
      } catch (error) {
        if (isUnmountedRef.current) {
          return '';
        }

        addNoticeBanner({ text: formatMessage(messages.awsWafTokenError), palette: 'caution' });

        const duration = performance.now() - startedAt;
        const errorMessage = getErrorMessage(error);

        track('waf.token.debug', { source: TOKEN_SOURCE, isFinal: true, error: errorMessage, duration }); // prettier-ignore
        datadog.logger.error('AWS WAF | Token error', { tokenSource: TOKEN_SOURCE, error: errorMessage, duration }); // prettier-ignore

        return '';
      } finally {
        tokenPromiseRef.current = undefined;
      }
    })();

    tokenPromiseRef.current = tokenPromise;

    return tokenPromise;
  };

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(trackMountState, [trackMountState]);

  return null;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function requestAwsWafToken() {
  await initializeAwsWafNativeSdk();

  return getAwsWafToken();
}

// ─── Constants ───────────────────────────────────────────────────────────────

const TOKEN_SOURCE = 'native-sdk';

// Matches the longest wait of the WebView flow (WebView ready + token response).
const TOKEN_RESPONSE_TIMEOUT_MS = 10_000;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  awsWafTokenError: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'AWS WAF | Error',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  generateAwsWafTokenRef: RefObject<() => Promise<string>>;
};
